import { Box, SimpleGrid, Text } from '@mantine/core';
import type { ReactElement } from 'react';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import type { AnalyticsSurface } from './AnalyticsSurface';
import { usePinnedTilesStore } from './usePinnedTiles';
import { useAssistFilters } from './assist/AssistSurfaceProvider';
import { AddTileViewModel } from './AddTileViewModel';
import AddTileCard from './AddTileCard';
import AddTileModal from './AddTileModal';
import PinnedTileCard from './PinnedTileCard';

/**
 * The "Your insights" region beneath a surface's gallery: the user's pinned
 * tiles followed by the always-present "Add insight" card. Pinning from AMA and
 * saving from the modal both write to the same {@link PinnedTilesStore}, so this
 * section is the single home for custom tiles on a surface.
 */
export default function PinnedTilesSection({ surface }: { surface: AnalyticsSurface }): ReactElement {
    const store = usePinnedTilesStore();
    const vm = useViewModel(AddTileViewModel);
    const pageFilters = useAssistFilters();
    const tiles = store.tilesFor(surface.id);

    const handleSave = (): void => {
        const spec = vm.currentSpec();
        if (spec === null) return;
        if (vm.editingTileId !== null) {
            store.update(vm.editingTileId, spec);
        } else {
            store.pin(surface.id, spec, 'add-insight');
        }
        vm.reset();
        vm.closeModal();
    };

    return (
        <Box mt="xl">
            <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="sm" style={{ letterSpacing: '0.08em' }}>
                Your insights
            </Text>
            <SimpleGrid cols={surface.gallery.columns} spacing="lg" style={{ alignItems: 'stretch' }}>
                {tiles.map((tile) => (
                    <PinnedTileCard
                        key={tile.id}
                        tile={tile}
                        basePath={surface.basePath}
                        onEdit={() => vm.openForEdit(tile)}
                        onRemove={() => store.remove(tile.id)}
                    />
                ))}
                <AddTileCard onClick={() => vm.openModal()} />
            </SimpleGrid>
            <AddTileModal vm={vm} surface={surface} pageFilters={pageFilters} onSave={handleSave} />
        </Box>
    );
}
